<template>
  <div id="flutterwave-add-bank" class="flutterwave-add-bank">
    <div class="form-header">
      <div class="header-icon">
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M3 21H21M3 10H21M5 6L12 3L19 6M4 10V21M20 10V21M8 14V17M12 14V17M16 14V17" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </div>
      <div class="header-content">
        <h3 class="header-title">Select your bank</h3>
        <p class="header-description">Link your {{ currencyLabel }} bank account for withdrawals.</p>
      </div>
    </div>

    <div v-if="loadingBanks" class="tw-flex tw-justify-center tw-py-8">
      <SmallLoader />
    </div>

    <form v-else @submit.prevent="addBank" class="bank-form">
      <div class="form-field">
        <label class="form-label">Bank Name</label>
        <div class="bank-search-wrapper">
          <div class="search-input-container">
            <svg class="search-icon" viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="1.5" />
              <path d="M16 16L20 20" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
            </svg>
            <input
              type="text"
              class="search-input"
              v-model="searchQuery"
              @focus="showDropdown = true"
              @input="showDropdown = true"
              placeholder="Search for your bank"
            />
          </div>
          <ul v-if="showDropdown && filteredBanks.length > 0" class="bank-dropdown">
            <li
              v-for="(bank, index) in filteredBanks"
              :key="index"
              class="bank-option"
              @click="selectBank(bank)"
            >
              {{ bank.name }}
            </li>
          </ul>
          <div v-if="showDropdown && filteredBanks.length === 0 && searchQuery" class="no-results">
            No banks found
          </div>
        </div>
      </div>
      <div class="form-field">
        <TextInput
          placeHolder="Account Number"
          :text="payload.account_number"
          type="text"
          name="accountNumber"
          id="accountNumber"
          label="Account Number"
          :showLabel="true"
          @set="setAccountNumber"
          @valid="accountNumberValid"
        />
      </div>
      <div class="form-field">
        <TextInput
          placeHolder="Account Name"
          :text="payload.account_name"
          type="text"
          name="accountName"
          id="accountName"
          label="Account Name"
          :showLabel="true"
          @set="setAccountName"
          @valid="accountNameValid"
        />
      </div>
      <Btn class="submit-btn" title="Add Bank" :loading="loading" />
    </form>
  </div>
</template>

<script>
import Btn from "@/components/general/BtnComponent.vue";
import TextInput from "@/components/general/TextInput";
import SmallLoader from "@/components/notification/SmallLoader";
import bankApi from "@/utils/addBankApi";
import { mapActions } from "vuex";

export default {
  name: "FlutterwaveAddBank",

  emits: ["added"],

  components: {
    TextInput,
    Btn,
    SmallLoader,
  },

  props: {
    currency: {
      type: String,
      default: "NGN",
    },
  },

  data() {
    return {
      loading: false,
      loadingBanks: false,
      banks: [],
      selectedBank: null,
      searchQuery: "",
      showDropdown: false,
      validInput: {
        account_name: false,
        account_number: false,
      },
      payload: {
        account_name: "",
        account_number: "",
        bank_name: "",
        currency: "",
        swift_code: "",
        sort_code: "",
        bank_address: "",
      },
    };
  },

  computed: {
    filteredBanks() {
      if (!this.searchQuery) {
        return this.banks;
      }
      return this.banks.filter((bank) =>
        bank.name.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    },
    currencyCode() {
      // Map currency to flutterwave country code
      const currencyMap = {
        NGN: "NG",
        KES: "KE",
        USD: "US",
        GHS: "GH",
        ZAR: "ZA",
        TZS: "TZ",
        UGX: "UG",
      };
      return currencyMap[this.currency] || "NG";
    },
    currencyLabel() {
      const labelMap = {
        NGN: "Nigerian",
        KES: "Kenyan",
        USD: "US",
        GHS: "Ghanaian",
        ZAR: "South African",
      };
      return labelMap[this.currency] || "";
    },
    countryName() {
      const countryMap = {
        NGN: "Nigeria",
        KES: "Kenya",
        USD: "United States",
        GHS: "Ghana",
        ZAR: "South Africa",
      };
      return countryMap[this.currency] || "";
    },
    // ISO country saved on the bank.
    countryCode() {
      const codeMap = {
        NGN: "NG",
        KES: "KE",
        USD: "US",
        GHS: "GH",
        ZAR: "ZA",
      };
      return codeMap[this.currency];
    },
  },

  mounted() {
    this.payload.currency = this.currency;
    this.fetchFlutterwaveBanks();
    
    // Close dropdown when clicking outside
    document.addEventListener("click", this.handleClickOutside);
  },

  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },

  watch: {
    currency(newVal) {
      this.payload.currency = newVal;
      this.banks = [];
      this.searchQuery = "";
      this.selectedBank = null;
      this.fetchFlutterwaveBanks();
    },
  },

  methods: {
    ...mapActions("Account", ["addBankAccount"]),
    ...mapActions(["showToast"]),

    async fetchFlutterwaveBanks() {
      this.loadingBanks = true;
      try {
        const response = await bankApi.fetchFlutterwaveBanks(this.currencyCode);
        if (response.data && response.data.data) {
          this.banks = response.data.data;
        } else if (response.data) {
          this.banks = response.data;
        }
        console.log("Flutterwave Banks Response:", this.banks);
      } catch (error) {
        console.error("Error fetching flutterwave banks:", error);
        this.showToast({
          description: "Failed to fetch banks. Please try again.",
          display: true,
          type: "error",
        });
      } finally {
        this.loadingBanks = false;
      }
    },

    setBankFromDropdown(bankName) {
      if (!bankName) return;
      const bank = this.banks.find((b) => b.name === bankName);
      if (bank) {
        this.selectedBank = bank;
        this.payload.bank_name = bank.name;
        // Store bank code if needed for future use
        this.payload.bank_code = bank.code;
      }
    },

    selectBank(bank) {
      this.selectedBank = bank;
      this.payload.bank_name = bank.name;
      this.payload.swift_code = bank.code;
      this.payload.sort_code = bank.code;
      this.payload.bank_address = this.countryName;
      this.searchQuery = bank.name;
      this.showDropdown = false;
    },

    handleClickOutside(event) {
      const dropdown = this.$el.querySelector(".tw-relative");
      if (dropdown && !dropdown.contains(event.target)) {
        this.showDropdown = false;
      }
    },

    setAccountName(text) {
      this.payload.account_name = text;
    },

    setAccountNumber(text) {
      this.payload.account_number = text;
    },

    accountNameValid(valid) {
      this.validInput.account_name = valid;
    },

    accountNumberValid(valid) {
      this.validInput.account_number = valid;
    },

    addBank() {
      setTimeout(async () => {
        if (!this.validInput.account_name || !this.validInput.account_number) {
          return this.showToast({
            description: "Please fill all input fields correctly",
            display: true,
            type: "error",
          });
        }

        if (!this.payload.bank_name) {
          return this.showToast({
            description: "Please select a bank",
            display: true,
            type: "error",
          });
        }

        const apiPayload = {
          account_name: this.payload.account_name,
          account_number: this.payload.account_number,
          bank_name: this.payload.bank_name,
          currency: this.payload.currency,
          swift_code: this.payload.swift_code,
          sort_code: this.payload.sort_code,
          bank_address: this.payload.bank_address,
          country: this.countryCode,
        };

        this.loading = true;
        try {
          const response = await this.addBankAccount(apiPayload);
          if (response) this.$emit("added", this.currency);
        } catch (error) {
          console.error("Error adding bank:", error);
        } finally {
          this.loading = false;
        }
      }, 500);
    },
  },
};
</script>

<style lang="scss" scoped>
.flutterwave-add-bank {
  padding: 8px 0;
  margin-top: 16px;
}

.form-header {
  display: flex;
  gap: 16px;
  padding: 16px;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  margin-bottom: 24px;
}

.header-icon {
  width: 44px;
  height: 44px;
  min-width: 44px;
  background: rgba(77, 132, 255, 0.1);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 22px;
    height: 22px;
    color: #4D84FF;
  }
}

.header-content {
  flex: 1;
}

.header-title {
  font-size: 16px;
  font-weight: 600;
  color: #0F172A;
  margin: 0 0 4px 0;
}

.header-description {
  font-size: 13px;
  color: #64748B;
  margin: 0;
  line-height: 1.4;
}

.bank-form {
  display: flex;
  flex-direction: column;
}

.form-field {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #64748B;
  margin-bottom: 8px;
}

.bank-search-wrapper {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 7px;
  overflow: hidden;
  transition: all 0.2s ease;

  &:focus-within {
    border-color: #4D84FF;
  }
}

.search-input-container {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 14px;
  width: 18px;
  height: 18px;
  color: #64748B;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 14px 16px 14px 44px;
  font-size: 14px;
  color: #0F172A;
  background: transparent;
  border: none;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: #94A3B8;
  }
}

.bank-dropdown {
  max-height: 200px;
  overflow-y: auto;
  margin: 0;
  padding: 0;
  list-style: none;
  border-top: 1px solid #E2E8F0;
}

.bank-option {
  padding: 12px 16px;
  font-size: 14px;
  color: #0F172A;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #F8FAFC;
  }
}

.no-results {
  padding: 12px 16px;
  font-size: 14px;
  color: #64748B;
  border-top: 1px solid #E2E8F0;
}

.selected-bank {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #4D84FF;
  margin: 8px 0 0 0;

  svg {
    width: 16px;
    height: 16px;
  }
}

.submit-btn {
  margin-top: 12px;
}
</style>
